import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Loader2, X, Briefcase, Search as SearchIcon } from 'lucide-react';
import { toast } from 'sonner';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';
import { CompanyPicker, ContactSelect, AgreementSelect } from './CRMPickers';
import CRMJobOrderDetail from './CRMJobOrderDetail';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_COLORS = {
  Draft: 'bg-gray-100 text-gray-700', Intake: 'bg-blue-100 text-blue-700', 'Active Search': 'bg-indigo-100 text-indigo-700',
  'On Hold': 'bg-amber-100 text-amber-700', Filled: 'bg-emerald-100 text-emerald-700', 'Closed / Lost': 'bg-red-100 text-red-700',
};
export const JobOrderStageBadge = ({ status }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-medium ${STAGE_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>{status || 'Draft'}</span>
);

const PRIORITY_COLORS = { Low: 'text-gray-400', Medium: 'text-blue-500', High: 'text-orange-500', Urgent: 'text-red-600' };

const CRMJobOrders = ({ embedded = false, navParams = {} }) => {
  const [config, setConfig] = useState({ stages: [], work_modes: [], priorities: [], employment_types: [], comp_frequencies: [] });
  const [jobOrders, setJobOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [stageCounts, setStageCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [noLinkOnly, setNoLinkOnly] = useState(() => navParams.no_link === 'true');
  const [overdueOnly, setOverdueOnly] = useState(() => navParams.overdue === 'true');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => { fetchWithAuth(`${API_URL}/api/crm/job-orders/config`).then(setConfig).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 100 });
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    if (priorityFilter) params.set('priority', priorityFilter);
    if (noLinkOnly) params.set('no_manatal_link', 'true');
    if (overdueOnly) params.set('overdue_only', 'true');
    fetchWithAuth(`${API_URL}/api/crm/job-orders?${params.toString()}`)
      .then(d => { setJobOrders(d.job_orders); setTotal(d.total); setStageCounts(d.stage_counts); }).catch(() => {}).finally(() => setLoading(false));
  }, [search, statusFilter, priorityFilter, noLinkOnly, overdueOnly]);

  useEffect(() => { load(); }, [load]);

  const isOverdue = (j) => j.next_action_due_date && j.next_action_due_date <= new Date().toISOString().slice(0, 10);
  const fmtFee = (f) => f ? `$${Math.round(f.min).toLocaleString()}–$${Math.round(f.max).toLocaleString()}` : '—';

  return (
    <IntelligenceLayout title="Job Orders" embedded={embedded}>
      <div className="p-6 space-y-4" data-testid="crm-job-orders-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="page-title">Job Orders</h1>
            <p className="text-sm text-gray-500 mt-1">{total.toLocaleString()} client engagements tracked</p>
          </div>
          <button onClick={() => setShowCreate(true)} data-testid="new-job-order-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90 transition-colors">
            <Plus className="h-4 w-4" /> New Job Order
          </button>
        </div>

        <div className="flex items-center flex-wrap gap-2" data-testid="job-orders-filters">
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search job title..." data-testid="job-order-search"
              className="pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37]" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="job-order-status-filter"
            className="text-xs font-medium px-3 py-2 border border-gray-200 rounded-lg text-gray-600 bg-white">
            <option value="">All stages</option>
            {config.stages.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} data-testid="job-order-priority-filter"
            className="text-xs font-medium px-3 py-2 border border-gray-200 rounded-lg text-gray-600 bg-white">
            <option value="">All priorities</option>
            {config.priorities.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 px-2 cursor-pointer" data-testid="no-manatal-link-toggle">
            <input type="checkbox" checked={noLinkOnly} onChange={e => setNoLinkOnly(e.target.checked)} className="rounded" /> No Manatal link
          </label>
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 px-2 cursor-pointer" data-testid="job-order-overdue-toggle">
            <input type="checkbox" checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)} className="rounded" /> Overdue next action
          </label>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : jobOrders.length === 0 ? (
            <p className="text-sm text-gray-400 p-8 text-center" data-testid="job-orders-empty-state">No job orders match these filters.</p>
          ) : (
            <table className="w-full text-sm" data-testid="job-orders-table">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left py-2.5 px-4">Job Title</th>
                  <th className="text-left py-2.5 px-4">Company</th>
                  <th className="text-left py-2.5 px-4">Stage</th>
                  <th className="text-left py-2.5 px-4">Priority</th>
                  <th className="text-left py-2.5 px-4">Est. Fee</th>
                  <th className="text-left py-2.5 px-4">Manatal</th>
                  <th className="text-left py-2.5 px-4">Next Action</th>
                </tr>
              </thead>
              <tbody>
                {jobOrders.map(j => (
                  <tr key={j.id} onClick={() => setSelectedId(j.id)} data-testid={`job-order-row-${j.id}`}
                    className="border-t border-gray-100 hover:bg-[#fdf9ef] cursor-pointer transition-colors">
                    <td className="py-2.5 px-4 font-medium text-[#0A1F44]">{j.job_title}</td>
                    <td className="py-2.5 px-4 text-gray-500">{j.company_name || '—'}</td>
                    <td className="py-2.5 px-4"><JobOrderStageBadge status={j.status} /></td>
                    <td className={`py-2.5 px-4 font-medium ${PRIORITY_COLORS[j.priority] || 'text-gray-500'}`}>{j.priority}</td>
                    <td className="py-2.5 px-4 text-gray-600">{fmtFee(j.estimated_fee)}</td>
                    <td className="py-2.5 px-4" data-testid={`job-order-manatal-status-${j.id}`}>
                      {j.manatal_job_id ? <span className="text-emerald-600 text-xs font-medium">Linked #{j.manatal_job_id}</span> : <span className="text-gray-400 text-xs">Not linked</span>}
                    </td>
                    <td className={`py-2.5 px-4 ${isOverdue(j) ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                      {j.next_action || '—'}{j.next_action_due_date ? ` (${j.next_action_due_date})` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {Object.keys(stageCounts).length > 0 && (
          <div className="flex flex-wrap gap-2 text-xs text-gray-500" data-testid="job-order-stage-counts-summary">
            {Object.entries(stageCounts).filter(([, n]) => n > 0).map(([s, n]) => <span key={s} className="px-2 py-1 bg-gray-50 rounded border border-gray-200">{s}: <b className="text-[#0A1F44]">{n}</b></span>)}
          </div>
        )}
      </div>

      {showCreate && <CreateJobOrderModal config={config} onClose={() => setShowCreate(false)} onCreated={(id) => { setShowCreate(false); load(); setSelectedId(id); }} />}
      {selectedId && <CRMJobOrderDetail jobOrderId={selectedId} config={config} onClose={() => setSelectedId(null)} onUpdated={load} />}
    </IntelligenceLayout>
  );
};

const CreateJobOrderModal = ({ config, onClose, onCreated }) => {
  const [company, setCompany] = useState(null);
  const [agreementId, setAgreementId] = useState(null);
  const [contactId, setContactId] = useState(null);
  const [form, setForm] = useState({
    job_title: '', location: '', work_mode: 'Onsite', comp_min: '', comp_max: '', comp_currency: 'USD',
    comp_frequency: 'Annual', employment_type: 'Full-Time', priority: 'Medium', status: 'Draft', num_openings: 1,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!company) { toast.error('Select a company'); return; }
    if (!form.job_title.trim()) { toast.error('Job title is required'); return; }
    setSaving(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/crm/job-orders`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form, company_id: company.id, agreement_id: agreementId, hiring_contact_id: contactId,
          comp_min: form.comp_min ? Number(form.comp_min) : null, comp_max: form.comp_max ? Number(form.comp_max) : null,
          num_openings: Number(form.num_openings),
        }),
      });
      toast.success('Job Order created');
      onCreated(res.id);
    } catch (e) {
      toast.error('Could not create job order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="create-job-order-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-[#0A1F44] flex items-center gap-2"><Briefcase className="h-4 w-4" /> New Job Order</h3>
          <button onClick={onClose} data-testid="close-create-job-order-modal"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <CompanyPicker value={company} onChange={setCompany} testId="job-order-company-picker" />
        <AgreementSelect companyId={company?.id} value={agreementId} onChange={setAgreementId} testId="job-order-agreement-select" />
        <ContactSelect companyId={company?.id} value={contactId} onChange={setContactId} testId="job-order-contact-select" placeholder="Hiring contact (optional)" />
        <input value={form.job_title} onChange={e => setForm({ ...form, job_title: e.target.value })} placeholder="Job title *"
          data-testid="job-order-title-input" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Location"
            data-testid="job-order-location-input" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <select value={form.work_mode} onChange={e => setForm({ ...form, work_mode: e.target.value })} data-testid="job-order-work-mode-select"
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
            {config.work_modes.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <input type="number" value={form.comp_min} onChange={e => setForm({ ...form, comp_min: e.target.value })} placeholder="Comp min"
            data-testid="job-order-comp-min-input" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input type="number" value={form.comp_max} onChange={e => setForm({ ...form, comp_max: e.target.value })} placeholder="Comp max"
            data-testid="job-order-comp-max-input" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <select value={form.comp_frequency} onChange={e => setForm({ ...form, comp_frequency: e.target.value })} data-testid="job-order-comp-frequency-select"
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
            {config.comp_frequencies.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.employment_type} onChange={e => setForm({ ...form, employment_type: e.target.value })} data-testid="job-order-employment-type-select"
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
            {config.employment_types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} data-testid="job-order-priority-select"
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
            {config.priorities.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="job-order-status-select"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
          {config.stages.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={submit} disabled={saving} data-testid="create-job-order-submit"
          className="w-full py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50">
          {saving ? 'Creating...' : 'Create Job Order'}
        </button>
      </div>
    </div>
  );
};

export default CRMJobOrders;
